import { ReactElement, useEffect, useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import toast from 'react-hot-toast';
import SectionHeader from '../../components/SectionHeader';
import Table from '../../components/Table';
import {
  FEEDBACK_STATUS,
  FEEDBACK_STATUS_LABELS,
  FEEDBACK_TYPE_LABELS,
  PRODUCT_AREA_LABELS,
  PRODUCT_AREA_OPTIONS,
  FEEDBACK_TYPE_OPTIONS,
} from '../../constants/feedback';
import { useFeedbackReports } from '../../hooks/useFeedback';
import type { FeedbackReport } from '../../api/feedback';
import FeedbackReportDetail from './FeedbackReportDetail';
import FeedbackSection from '../AdminView/FeedbackSection';
import { selectClass } from '../../components/formControlClasses';

/**
 * Triage view for user feedback (OMNISCIENT only — the route carries the
 * userFilter).
 *
 * Without somewhere to read reports the feedback table is a write-only
 * inbox, so this is not an optional extra: it is the half of the feature
 * that makes filing worthwhile.
 */
export default function FeedbackTriage(): ReactElement {
  const [statusFilter, setStatusFilter] = useState<string>(String(FEEDBACK_STATUS.NEW));
  const [areaFilter, setAreaFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [openReportId, setOpenReportId] = useState<string | null>(null);

  const params = useMemo(() => {
    const query: Record<string, string> = {};
    if (statusFilter) query.status = statusFilter;
    if (areaFilter) query.product_area = areaFilter;
    if (typeFilter) query.feedback_type = typeFilter;
    return query;
  }, [statusFilter, areaFilter, typeFilter]);

  const { data: reports, isPending, error } = useFeedbackReports(params);

  const columns = useMemo(() => {
    const helper = createColumnHelper<FeedbackReport>();
    return [
      helper.accessor('created_at', {
        header: 'Filed',
        cell: (info) => new Date(info.getValue()).toLocaleString(),
      }),
      helper.accessor('feedback_type', {
        header: 'Type',
        cell: (info) => FEEDBACK_TYPE_LABELS[info.getValue()] ?? info.getValue(),
      }),
      helper.accessor('product_area', {
        header: 'Area',
        cell: (info) => PRODUCT_AREA_LABELS[info.getValue()] ?? info.getValue(),
      }),
      helper.accessor('user_name', {
        header: 'From',
        cell: (info) => {
          const team = info.row.original.team_name;
          return `${info.getValue() ?? 'Unknown'}${team ? ` (${team})` : ''}`;
        },
      }),
      helper.accessor('title', { header: 'Summary' }),
      helper.accessor('status', {
        header: 'Status',
        cell: (info) => FEEDBACK_STATUS_LABELS[info.getValue()] ?? info.getValue(),
      }),
      helper.accessor('ticket_ref', {
        header: 'Ticket',
        cell: (info) => info.getValue() || '—',
      }),
    ];
  }, []);

  useEffect(() => {
    if (error) toast.error('Could not load feedback reports');
  }, [error]);

  return (
    <div className="cr-flex cr-flex-col cr-gap-4">
      <SectionHeader title="Feedback" />

      <div className="cr-flex cr-flex-wrap cr-items-end cr-gap-3">
        <label className="cr-flex cr-flex-col cr-text-sm cr-font-semibold cr-text-blue-900">
          Status
          <select className={selectClass} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value={String(FEEDBACK_STATUS.NEW)}>New</option>
            <option value={String(FEEDBACK_STATUS.PROCESSED)}>Processed</option>
            <option value="">All</option>
          </select>
        </label>
        <label className="cr-flex cr-flex-col cr-text-sm cr-font-semibold cr-text-blue-900">
          Product area
          <select className={selectClass} value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}>
            <option value="">All</option>
            {PRODUCT_AREA_OPTIONS.map((option) => (
              <option key={option.value} value={String(option.value)}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className="cr-flex cr-flex-col cr-text-sm cr-font-semibold cr-text-blue-900">
          Type
          <select className={selectClass} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All</option>
            {FEEDBACK_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={String(option.value)}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>

      <Table
        data={reports ?? []}
        columns={columns}
        isLoading={isPending}
        getRowId={(row) => row.id}
        onRowClick={(_event, row) => setOpenReportId(row.id)}
      />

      {/* The older ticket-list card, moved here when Admin gained a submenu.
          It overlaps this table and is kept only until one of the two is
          settled on — a collapsed card, so it costs nothing until opened. */}
      <FeedbackSection />

      {openReportId && (
        <FeedbackReportDetail reportId={openReportId} onClose={() => setOpenReportId(null)} />
      )}
    </div>
  );
}
